<script setup lang="ts">
/**
 * The book's outline. There is no chapter title field: a chapter is labelled positionally and
 * described by the opening characters of its first text block.
 */

import { faArrowDown, faArrowUp, faTrashCan } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { BtnIcon, BtnSecondary } from '@shared/ui/buttons'

import { useChapterList } from '../../../model/assignments/chapter-list.hook'

defineOptions({
  name: 'ChapterList',
})

const {
  refChapters,
  refHasChapters,
  selectChapter,
  addChapter,
  removeChapter,
  moveChapterUp,
  moveChapterDown,
} = useChapterList()
</script>

<template>
  <div class="chapter-list">
    <AppText name="body1-medium">
      Chapters
    </AppText>

    <AppText
      v-if="!refHasChapters"
      name="body2"
      color="slate-500"
    >
      No chapters yet. Add the first one to start writing.
    </AppText>

    <ul class="chapter-list__items">
      <!-- Chapters are addressed by position: `data` is unvalidated (decision 13), so a stored
           chapter may carry no readable `id`, and the numbering is the author's only handle on it. -->
      <li
        v-for="chapter in refChapters"
        :key="chapter.id || `chapter-${chapter.position}`"
        :class="['chapter-list__item', { 'is-selected': chapter.isSelected }]"
      >
        <div class="chapter-list__head">
          <button
            type="button"
            class="chapter-list__select"
            @click="selectChapter(chapter.index)"
          >
            <AppText
              name="body2-medium"
              truncate
            >
              {{ chapter.label }}
            </AppText>
            <AppText
              name="body2"
              color="slate-500"
              truncate
            >
              {{ chapter.summary }}
            </AppText>
          </button>
          <div class="chapter-list__actions">
            <BtnIcon
              :icon="faArrowUp"
              size="small"
              title="Move chapter up"
              :disabled="chapter.isFirstInOrder"
              @click="moveChapterUp(chapter.index)"
            />
            <BtnIcon
              :icon="faArrowDown"
              size="small"
              title="Move chapter down"
              :disabled="chapter.isLastInOrder"
              @click="moveChapterDown(chapter.index)"
            />
            <BtnIcon
              :icon="faTrashCan"
              size="small"
              color="red"
              title="Remove chapter"
              @click="removeChapter(chapter.index)"
            />
          </div>
        </div>
      </li>
    </ul>

    <BtnSecondary
      text="Add chapter"
      element-width="full"
      @click="addChapter"
    />
  </div>
</template>

<style scoped lang="scss" src="./ChapterList.scss" />
